import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { supabase } from '../lib/supabase'
import { 
  UserPlus, 
  Mail, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  RefreshCw,
  Eye,
  UserCheck,
  UserX
} from 'lucide-react'

const TestApprovalWorkflow = () => {
  const [testUsers, setTestUsers] = useState([])
  const [loading, setLoading] = useState(false)
  const [testResults, setTestResults] = useState([])
  const [currentStep, setCurrentStep] = useState(0)

  // Test user data
  const testUserData = {
    email: `test.user.${Date.now()}@testagency.gov`,
    password: 'TestPassword123!',
    name: 'Test User',
    agency: 'Test Government Agency',
    position: 'IT Security Officer',
    department: 'Information Technology',
    phone: '+639123456789',
    user_type: 'government_employee'
  }

  const testSteps = [
    'Register new test user',
    'Verify user is in pending status',
    'Check admin dashboard for pending user',
    'Approve user registration',
    'Verify approval email notification',
    'Test login with approved user',
    'Clean up test data'
  ]

  const runApprovalWorkflowTest = async () => {
    setLoading(true)
    setTestResults([])
    setCurrentStep(0)

    try {
      // Step 1: Register new test user
      setCurrentStep(1)
      addTestResult('info', 'Step 1: Registering new test user...')
      
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: testUserData.email,
        password: testUserData.password,
        options: {
          data: {
            name: testUserData.name,
            agency: testUserData.agency,
            position: testUserData.position,
            department: testUserData.department,
            phone: testUserData.phone,
            user_type: testUserData.user_type
          }
        }
      })

      if (authError) {
        addTestResult('error', `Registration failed: ${authError.message}`)
        return
      }

      addTestResult('success', `User registered successfully: ${testUserData.email}`)

      // Wait a moment for database triggers
      await new Promise(resolve => setTimeout(resolve, 2000))

      // Step 2: Verify user is in pending status
      setCurrentStep(2)
      addTestResult('info', 'Step 2: Verifying user is in pending status...')
      
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('id, email, name, approval_status, user_type')
        .eq('email', testUserData.email)
        .single()

      if (userError) {
        addTestResult('error', `Failed to fetch user data: ${userError.message}`)
        return
      }

      if (userData.approval_status === 'pending') {
        addTestResult('success', `User is correctly in pending status`)
        setTestUsers([userData])
      } else {
        addTestResult('error', `User approval status is '${userData.approval_status}', expected 'pending'`)
        return
      }

      // Step 3: Check if user appears in admin dashboard
      setCurrentStep(3)
      addTestResult('info', 'Step 3: Checking admin dashboard for pending user...')
      
      const { data: pendingUsers, error: pendingError } = await supabase
        .from('users')
        .select(`
          *,
          user_registration_approvals!inner(*)
        `)
        .eq('user_registration_approvals.status', 'pending')
        .eq('email', testUserData.email)

      if (pendingError) {
        addTestResult('warning', `Could not fetch pending users: ${pendingError.message}`)
      } else if (pendingUsers.length > 0) {
        addTestResult('success', `User found in pending users list for admin review`)
      } else {
        addTestResult('warning', `User not found in pending users list`)
      }

      // Step 4: Simulate admin approval (manual step)
      setCurrentStep(4)
      addTestResult('info', 'Step 4: Ready for admin approval. Please approve the user in the admin dashboard, then click "Continue"')
      
      // Wait for manual approval
      await waitForManualApproval(userData.id)

      // Step 5: Verify approval and email notification
      setCurrentStep(5)
      addTestResult('info', 'Step 5: Verifying approval status and checking for email notification...')
      
      const { data: approvedUser, error: approvedError } = await supabase
        .from('users')
        .select('id, email, name, approval_status')
        .eq('id', userData.id)
        .single()

      if (approvedError) {
        addTestResult('error', `Failed to fetch approved user: ${approvedError.message}`)
        return
      }

      if (approvedUser.approval_status === 'approved') {
        addTestResult('success', `User has been approved successfully`)
      } else {
        addTestResult('error', `User approval status is '${approvedUser.approval_status}', expected 'approved'`)
        return
      }

      // Step 6: Test login with approved user
      setCurrentStep(6)
      addTestResult('info', 'Step 6: Testing login with approved user...')
      
      const { error: loginError } = await supabase.auth.signInWithPassword({
        email: testUserData.email,
        password: testUserData.password
      })

      if (loginError) {
        addTestResult('error', `Login failed: ${loginError.message}`)
      } else {
        addTestResult('success', `Login successful - approval workflow is working correctly`)
      }

      // Step 7: Clean up
      setCurrentStep(7)
      addTestResult('info', 'Step 7: Cleaning up test data...')
      
      await cleanupTestData(userData.id)
      addTestResult('success', 'Test completed successfully!')

    } catch (error) {
      addTestResult('error', `Test failed: ${error.message}`)
    } finally {
      setLoading(false)
      setCurrentStep(0)
    }
  }

  const waitForManualApproval = (userId) => {
    return new Promise((resolve) => {
      const checkInterval = setInterval(async () => {
        const { data } = await supabase
          .from('users')
          .select('approval_status')
          .eq('id', userId)
          .single()
        
        if (data?.approval_status === 'approved') {
          clearInterval(checkInterval)
          resolve(true)
        }
      }, 3000) // Check every 3 seconds

      // Timeout after 2 minutes
      setTimeout(() => {
        clearInterval(checkInterval)
        resolve(false)
      }, 120000)
    })
  }

  const cleanupTestData = async (userId) => {
    try {
      // Delete user registration approvals
      await supabase
        .from('user_registration_approvals')
        .delete()
        .eq('user_id', userId)

      // Delete user
      await supabase
        .from('users')
        .delete()
        .eq('id', userId)

      // Sign out
      await supabase.auth.signOut()
    } catch (error) {
      console.error('Cleanup error:', error)
    }
  }

  const addTestResult = (type, message) => {
    setTestResults(prev => [...prev, { type, message, timestamp: new Date().toLocaleTimeString() }])
  }

  const getResultIcon = (type) => {
    switch (type) {
      case 'success': return <CheckCircle className="h-5 w-5 text-green-500" />
      case 'error': return <XCircle className="h-5 w-5 text-red-500" />
      case 'warning': return <AlertCircle className="h-5 w-5 text-yellow-500" />
      default: return <Clock className="h-5 w-5 text-blue-500" />
    }
  }

  return (
    <div className="min-h-screen bg-gov-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gov-800 mb-2">User Approval Workflow Test</h1>
          <p className="text-gov-600">Test the complete user registration and approval process</p>
        </div>

        {/* Test Controls */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gov-800">Test Controls</h2>
            {currentStep > 0 && (
              <div className="flex items-center text-sm text-gov-600">
                <Clock className="h-4 w-4 mr-2" />
                Step {currentStep} of {testSteps.length}: {testSteps[currentStep - 1]}
              </div>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <h3 className="font-medium text-gov-700 mb-2">Test User Details:</h3>
              <div className="bg-gov-50 p-4 rounded-lg text-sm">
                <p><strong>Email:</strong> {testUserData.email}</p>
                <p><strong>Name:</strong> {testUserData.name}</p>
                <p><strong>Agency:</strong> {testUserData.agency}</p>
                <p><strong>Type:</strong> {testUserData.user_type}</p>
              </div>
            </div>
            
            <div>
              <h3 className="font-medium text-gov-700 mb-2">Test Steps:</h3>
              <ol className="text-sm space-y-1">
                {testSteps.map((step, index) => (
                  <li key={index} className={`flex items-center ${
                    currentStep > index + 1 ? 'text-green-600' : 
                    currentStep === index + 1 ? 'text-blue-600 font-medium' : 'text-gov-600'
                  }`}>
                    <span className="w-5 h-5 rounded-full bg-gov-200 text-xs flex items-center justify-center mr-2">
                      {currentStep > index + 1 ? '✓' : index + 1}
                    </span>
                    {step}
                  </li>
                ))}
              </ol>
            </div>
          </div>

          <div className="flex space-x-4">
            <button
              onClick={runApprovalWorkflowTest}
              disabled={loading}
              className="flex items-center px-6 py-3 bg-cyber-600 text-white rounded-lg hover:bg-cyber-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <RefreshCw className="h-5 w-5 mr-2 animate-spin" />
                  Running Test...
                </>
              ) : (
                <>
                  <UserCheck className="h-5 w-5 mr-2" />
                  Run Approval Workflow Test
                </>
              )}
            </button>
            
            <button
              onClick={() => {
                setTestResults([])
                setCurrentStep(0)
                setTestUsers([])
              }}
              disabled={loading}
              className="flex items-center px-6 py-3 bg-gov-600 text-white rounded-lg hover:bg-gov-700 disabled:opacity-50"
            >
              <RefreshCw className="h-5 w-5 mr-2" />
              Clear Results
            </button>
          </div>
        </div>

        {/* Test Results */}
        {testResults.length > 0 && (
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-semibold text-gov-800 mb-4">Test Results</h2>
            
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {testResults.map((result, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  className="flex items-start p-3 rounded-lg bg-gov-50 border border-gov-200"
                >
                  <div className="mr-3 mt-0.5">
                    {getResultIcon(result.type)}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gov-800">{result.message}</p>
                    <p className="text-xs text-gov-500 mt-1">{result.timestamp}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-6">
          <h3 className="font-semibold text-blue-800 mb-3 flex items-center">
            <AlertCircle className="h-5 w-5 mr-2" />
            Test Instructions
          </h3>
          <ul className="text-sm text-blue-700 space-y-2">
            <li>• This test will create a new user account and walk through the approval workflow</li>
            <li>• When prompted, you will need to manually approve the user in the admin dashboard</li>
            <li>• The test will verify that email notifications are sent (check your email configuration)</li>
            <li>• Test data will be automatically cleaned up after completion</li>
            <li>• Make sure you have admin access to approve the test user</li>
          </ul>
        </div>
      </div>
    </div>
  )
}

export default TestApprovalWorkflow